import {
    skipToken,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useSuspenseQuery
} from '@tanstack/react-query';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {
    Distribution,
    ExpenditureReportV3,
    GetForecastDates,
    GmvStatsQuery,
    GmvExpenditureReport,
    AustralianMediaValueCsv,
    Comparison,
    GlobalMvDistribution,
    GlobalMediaValueCsv,
    GlobalComparison
} from './graphqlQueries';
import {
    MediaValueBreakdownColumn,
    MediaValueDateRangeFilter,
    MediaValueRowFilterInput,
    PeriodSelection,
    MediaValueQueryRowsV3Args,
    MediaValueCsvV3QueryVariables,
    GlobalMediaValueDateRangeFilter,
    GlobalMediaValueMeta,
    GlobalMediaValuePageInfo,
    SupportedCountry as GraphqlSupportedCountry,
    GmvExpenditureReportQueryVariables
} from '~/graphql-react-query/graphql';
import {add, now, startOf} from 'stringdate';
import {
    AdType,
    MediaType,
    MediaOwner,
    Industry,
    Brand,
    Category,
    Region,
    Publisher,
    Product,
    createPollingGenerator,
    Publication
} from 'bigdatr-style';
import Segment from '~/segment/data/Segment';
import {useCountry} from '~/feature/country/CountryContext';
import {AU, NZ} from 'bigdatr-style';
import {toGmvCountry, ensureColumnsAreGmvColumns} from '~/feature/media-value/helpers';

type DistributionPayload = {
    breakdownColumn?: MediaValueBreakdownColumn;
    rows: MediaValueRowFilterInput[];
    dateRangeFilter?: MediaValueDateRangeFilter;
};

type ComparisonPayload = {
    periodSelection?: PeriodSelection;
    rows: MediaValueRowFilterInput[];
};

export function useExpenditureReportV3Request(
    payload: MediaValueQueryRowsV3Args & {
        adType: boolean;
        brand: boolean;
        campaign: boolean;
        category: boolean;
        industry: boolean;
        mediaOwner: boolean;
        mediaType: boolean;
        product: boolean;
        publisher: boolean;
        region: boolean;
        segment: boolean;
        publication?: boolean;
    },
    options?: {enabled?: boolean}
) {
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;

    const {
        columns,
        columnFilter,
        pagination,
        excludeList,
        changesetId,
        dateRangeFilter,
        ...includeColumnsInResponse
    } = payload;

    const exclusiveDateRangeFilter = dateRangeFilter
        ? makeDateRangeExclusive(dateRangeFilter)
        : null;
    return useInfiniteQuery({
        queryKey: [
            'expenditureReport',
            columns,
            columnFilter,
            excludeList,
            pagination,
            pagination?.orderBy,
            exclusiveDateRangeFilter,
            exclusiveDateRangeFilter?.absolute,
            exclusiveDateRangeFilter?.relative,
            includeColumnsInResponse,
            changesetId
        ],
        queryFn: enabled
            ? async ({pageParam}) => {
                  const response = await request(ExpenditureReportV3, {
                      columns,
                      columnFilter,
                      dateRangeFilter: exclusiveDateRangeFilter,
                      pagination: {...pagination, pageNum: pageParam},
                      changesetId,
                      excludeFilter: excludeList,
                      adType: includeColumnsInResponse.adType ?? false,
                      brand: includeColumnsInResponse.brand ?? false,
                      campaign: includeColumnsInResponse.campaign ?? false,
                      category: includeColumnsInResponse.category ?? false,
                      industry: includeColumnsInResponse.industry ?? false,
                      mediaOwner: includeColumnsInResponse.mediaOwner ?? false,
                      mediaType: includeColumnsInResponse.mediaType ?? false,
                      product: includeColumnsInResponse.product ?? false,
                      publisher: includeColumnsInResponse.publisher ?? false,
                      region: includeColumnsInResponse.region ?? false,
                      segment: includeColumnsInResponse.segment ?? false,
                      publication: includeColumnsInResponse.publication ?? false
                  });

                  const data = response?.mediaValue.rowsV3;
                  if (!data) {
                      throw new Error('Media Values not returned from API');
                  }

                  // the v3 endpoint does not return a labels object because the labels are
                  // contained within the rows themselves. this simplifies the api quite a lot,
                  // however the issue is that we are in a transition period between media value v1,
                  // v2 and v3, so it is easier to make the v3 conform to the v1 and v2 data
                  // structure on the client instead of complicating other areas of the app WAY too
                  // much. a media value rework/redesign is in the works so i dont want to over
                  // engineer something that might be deleted in the near future
                  const adTypes = new Set(
                      data.rows.flatMap((row) => (row.adType ? [row.adType] : []))
                  );
                  const brands = new Set(
                      data.rows.flatMap((row) => (row.brand ? [row.brand] : []))
                  );
                  const categories = new Set(
                      data.rows.flatMap((row) => (row.category ? [row.category] : []))
                  );
                  const industries = new Set(
                      data.rows.flatMap((row) => (row.industry ? [row.industry] : []))
                  );
                  const mediaOwners = new Set(
                      data.rows.flatMap((row) => (row.mediaOwner ? [row.mediaOwner] : []))
                  );
                  const mediaTypes = new Set(
                      data.rows.flatMap((row) => (row.mediaType ? [row.mediaType] : []))
                  );
                  const products = new Set(
                      data.rows.flatMap((row) => (row.product ? [row.product] : []))
                  );
                  const publishers = new Set(
                      data.rows.flatMap((row) => (row.publisher ? [row.publisher] : []))
                  );
                  const regions = new Set(
                      data.rows.flatMap((row) => (row.region ? [row.region] : []))
                  );

                  const labels = {
                      adType: [...adTypes].map(
                          (name) => new AdType({name, canonicalName: name, id: name})
                      ),
                      brand: [...brands].map((name) => new Brand({name})),
                      category: [...categories].map((name) => new Category({name})),
                      industry: [...industries].map((name) => new Industry({name})),
                      mediaOwner: [...mediaOwners].map((name) => new MediaOwner({name, id: name})),
                      mediaType: [...mediaTypes].map(
                          (name) => new MediaType({name, canonicalName: name, id: name})
                      ),
                      product: [...products].map((name) => new Brand({name})),
                      publisher: [...publishers].map((name) => new Publisher({name, id: name})),
                      region: [...regions].map((name) => new Region({name})),
                      // @ts-ignore media value api doesnt return the full segment data. but it doesnt
                      // matter in this instance as all we need is the name, color and id
                      segment: (data.labels.segment || []).map((segment) => new Segment(segment))
                  };

                  return {
                      ...data,
                      labels
                  };
              }
            : skipToken,
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const hasNextPage = lastPage.pageInfo.hasNextPage;
            const currentPage = lastPage.pageInfo?.currentPage;
            if (hasNextPage && currentPage) return currentPage + 1;
            return null;
        }
    });
}

export type GmvExpenditureReportPayload = Omit<
    GmvExpenditureReportQueryVariables,
    | 'includeColumnTotals'
    | 'adType'
    | 'brand'
    | 'category'
    | 'industry'
    | 'mediaType'
    | 'product'
    | 'publication'
    | 'region'
> & {
    includeColumnTotals?: boolean;
    adType?: boolean;
    brand?: boolean;
    category?: boolean;
    industry?: boolean;
    mediaType?: boolean;
    product?: boolean;
    publication?: boolean;
    region?: boolean;
};

export type GmvExpenditureReportCell = {
    date: string;
    value: number;
    adType?: string | null;
    brand?: string | null;
    category?: string | null;
    industry?: string | null;
    mediaType?: string | null;
    product?: string | null;
    publication?: string | null;
    region?: string | null;
};

export type GmvLabels = {
    adType?: AdType[];
    brand?: Brand[];
    category?: Category[];
    industry?: Industry[];
    mediaType?: MediaType[];
    product?: Product[];
    publication?: Publication[];
    region?: Region[];
};

type GmvExpenditureReportFlattened = {
    rows: GmvExpenditureReportCell[];
    labels: GmvLabels;
    columnTotals: Array<{column: string; value: number}>;
    grandTotal: number;
    pageInfo: GlobalMediaValuePageInfo;
    meta: GlobalMediaValueMeta;
};

const DEFAULT_GMV_PAGE_INFO: GlobalMediaValuePageInfo = {
    currentPage: 1,
    hasNextPage: false,
    hasPreviousPage: false
};

const DEFAULT_GMV_META: GlobalMediaValueMeta = {isTruncated: false};

function buildGmvLabelsFromCells(cells: GmvExpenditureReportCell[]): GmvLabels {
    const adTypes = [...new Set(cells.flatMap((c) => (c.adType ? [c.adType] : [])))].map(
        (name) => new AdType({name, canonicalName: name, id: name})
    );
    const brands = [...new Set(cells.flatMap((c) => (c.brand ? [c.brand] : [])))].map(
        (name) => new Brand({name})
    );
    const categories = [...new Set(cells.flatMap((c) => (c.category ? [c.category] : [])))].map(
        (name) => new Category({name})
    );
    const industries = [...new Set(cells.flatMap((c) => (c.industry ? [c.industry] : [])))].map(
        (name) => new Industry({name})
    );
    const mediaTypes = [...new Set(cells.flatMap((c) => (c.mediaType ? [c.mediaType] : [])))].map(
        (name) => new MediaType({name, canonicalName: name, id: name})
    );
    const products = [...new Set(cells.flatMap((c) => (c.product ? [c.product] : [])))].map(
        (name) => new Product({name})
    );
    const publications = [
        ...new Set(cells.flatMap((c) => (c.publication ? [c.publication] : [])))
    ].map((name) => new Publication({name, id: name}));
    const regions = [...new Set(cells.flatMap((c) => (c.region ? [c.region] : [])))].map(
        (name) => new Region({name})
    );
    return {
        adType: adTypes.length ? adTypes : undefined,
        brand: brands.length ? brands : undefined,
        category: categories.length ? categories : undefined,
        industry: industries.length ? industries : undefined,
        mediaType: mediaTypes.length ? mediaTypes : undefined,
        product: products.length ? products : undefined,
        publication: publications.length ? publications : undefined,
        region: regions.length ? regions : undefined
    };
}

export function useGlobalMediaValueExpenditureReportRequest(
    payload: GmvExpenditureReportPayload & {country: GraphqlSupportedCountry},
    options?: {enabled?: boolean}
) {
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;

    const {
        country,
        columns,
        columnFilter,
        excludeFilter,
        dateRangeFilter,
        pagination,
        includeColumnTotals = true,
        adType = false,
        brand = false,
        category = false,
        industry = false,
        mediaType = false,
        product = false,
        publication = false,
        region = false
    } = payload;

    const exclusiveDateRangeFilter = dateRangeFilter
        ? makeGmvDateRangeExclusive(dateRangeFilter)
        : null;

    return useInfiniteQuery({
        queryKey: [
            'gmvExpenditureReport',
            country,
            columns,
            columnFilter,
            excludeFilter,
            pagination,
            pagination?.orderBy,
            exclusiveDateRangeFilter,
            includeColumnTotals,
            adType,
            brand,
            category,
            industry,
            mediaType,
            product,
            publication,
            region
        ],
        queryFn: enabled
            ? async ({pageParam}) => {
                  const response = await request(GmvExpenditureReport, {
                      country,
                      columns,
                      columnFilter,
                      excludeFilter,
                      dateRangeFilter: exclusiveDateRangeFilter,
                      pagination: {...pagination, pageNum: pageParam},
                      includeColumnTotals,
                      adType,
                      brand,
                      category,
                      industry,
                      mediaType,
                      product,
                      publication,
                      region
                  });

                  const data = response?.globalMediaValue?.expenditureReport;
                  if (!data) {
                      throw new Error(
                          'Global Media Value expenditure report not returned from API'
                      );
                  }
                  return data;
              }
            : skipToken,
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const hasNextPage = lastPage.pageInfo.hasNextPage;
            const currentPage = lastPage.pageInfo?.currentPage;
            if (hasNextPage && currentPage) return currentPage + 1;
            return null;
        },
        select: (data) => {
            const pages = data.pages;
            const allCells: GmvExpenditureReportCell[] = pages.flatMap((p) => p.cells ?? []);
            const lastPage = pages[pages.length - 1];
            const flattened: GmvExpenditureReportFlattened = {
                rows: allCells,
                labels: buildGmvLabelsFromCells(allCells),
                columnTotals: lastPage?.columnTotals ?? [],
                grandTotal: lastPage?.grandTotal ?? 0,
                pageInfo: lastPage?.pageInfo ?? DEFAULT_GMV_PAGE_INFO,
                meta: lastPage?.meta ?? DEFAULT_GMV_META
            };
            return {...data, flattened};
        }
    });
}

export function useExpenditureReportCsvDownload() {
    const request = useClientMainAuthorizedFetch();
    const {country} = useCountry();
    return useMutation({
        mutationFn: async (payload: MediaValueCsvV3QueryVariables) => {
            const pollingData = createPollingGenerator({
                createRequest: async () => {
                    const exclusiveDateRangeFilter = payload.dateRangeFilter
                        ? makeDateRangeExclusive(payload.dateRangeFilter)
                        : null;

                    if (country === AU) {
                        const auResponse = await request(AustralianMediaValueCsv, {
                            ...payload,
                            dateRangeFilter: exclusiveDateRangeFilter
                        });

                        const signedUrl = auResponse.mediaValue.csvV3.csv;
                        if (!signedUrl) {
                            throw new Error('CSV signed URL not returned from API');
                        }
                        return signedUrl;
                    }

                    const graphqlCountryInput = toGmvCountry(country);

                    const {columns, ...otherPayload} = payload;
                    const globalResponse = await request(GlobalMediaValueCsv, {
                        ...otherPayload,
                        columns: ensureColumnsAreGmvColumns(columns),
                        dateRangeFilter: exclusiveDateRangeFilter,
                        country: graphqlCountryInput
                    });

                    const signedUrl = globalResponse.globalMediaValue.csv.csv;
                    if (!signedUrl) {
                        throw new Error('CSV signed URL not returned from API');
                    }
                    return signedUrl;
                },
                pollRequest: async (createResponse) => {
                    const response = await fetch(createResponse);
                    return {status: response.status, downloadUrl: createResponse};
                },
                pollWhile: (data) => data?.status === 404,
                interval: 2000
            });

            // Can side step this by flattening the whole request to one promise
            let lastPayload;
            for await (const payload of pollingData) {
                lastPayload = payload;
            }

            return lastPayload.downloadUrl;
        }
    });
}

export function useDistribution(payload: DistributionPayload, options: {enabled?: boolean}) {
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? false;
    const {country} = useCountry();

    // removing un-necessary fields from row
    const requestRows = removeNonUsedColumns(payload.rows);

    const graphqlCountryInput = toGmvCountry(country);

    const {breakdownColumn, dateRangeFilter} = payload || {};

    return useQuery({
        queryKey: [
            'distribution',
            graphqlCountryInput,
            requestRows,
            breakdownColumn,
            dateRangeFilter
        ],
        queryFn: enabled
            ? async () => {
                  if (graphqlCountryInput === 'AU') {
                      const response = await request(Distribution, {
                          rows: requestRows,
                          breakdownColumn,
                          dateRangeFilter: dateRangeFilter
                              ? makeDateRangeExclusive(dateRangeFilter)
                              : null
                      });

                      if (!response?.mediaValue.distributionV3) {
                          throw new Error('Distribution not returned from API');
                      }

                      return response.mediaValue.distributionV3;
                  }

                  if (!breakdownColumn || !dateRangeFilter) {
                      throw new Error('Breakdown column and date range filter are required');
                  }
                  if (breakdownColumn === 'mediaOwner') {
                      throw new Error('Media owner is not supported for global media value');
                  }

                  const response = await request(GlobalMvDistribution, {
                      country: graphqlCountryInput,
                      rows: requestRows,
                      breakdownColumn,
                      dateRangeFilter: makeDateRangeExclusive(dateRangeFilter)
                  });

                  if (!response?.globalMediaValue.distribution) {
                      throw new Error('Distribution not returned from API');
                  }

                  return response.globalMediaValue.distribution;
              }
            : skipToken
    });
}

export function useComparison(payload: ComparisonPayload, options: {enabled?: boolean}) {
    const request = useClientMainAuthorizedFetch();
    const {country} = useCountry();
    const enabled = options?.enabled ?? false;

    // Make periodSelection exclusive
    const exclusivePeriodSelection = makePeriodSelectionExclusive(payload?.periodSelection);

    // removing un-necessary fields from row
    const requestRows = removeNonUsedColumns(payload.rows);

    return useQuery({
        queryKey: [
            'comparisonMediaValue',
            country,
            requestRows,
            payload.rows,
            payload.periodSelection,
            exclusivePeriodSelection
        ],
        queryFn: enabled
            ? async () => {
                  if (country === AU) {
                      const response = await request(Comparison, {
                          periodSelection: exclusivePeriodSelection,
                          rows: requestRows
                      });

                      if (!response?.mediaValue.comparisonV3) {
                          throw new Error('Comparison not returned from API');
                      }

                      return response.mediaValue.comparisonV3;
                  }

                  const globalResponse = await request(GlobalComparison, {
                      country: toGmvCountry(country),
                      periodSelection: exclusivePeriodSelection,
                      rows: requestRows
                  });

                  if (!globalResponse?.globalMediaValue.comparison) {
                      throw new Error('Comparison not returned from API');
                  }

                  return globalResponse.globalMediaValue.comparison;
              }
            : skipToken
    });
}

export function useGetForecastDates(options: {enabled: boolean}) {
    const request = useClientMainAuthorizedFetch();
    const enabled = options.enabled;
    // we are always use opensearch
    const queryOpensearch = true;
    const {country} = useCountry();

    return useSuspenseQuery({
        queryKey: ['getForecastDates', country, enabled, queryOpensearch],
        queryFn: async () => {
            // we cannot conditionally pause suspense queries. however to avoid a major code
            // restructure, using a suspense query significantly simplifies the code because a
            // suspense query will throw promises to the nearest Suspense boundary. this allows us
            // to show a Loader until the forecast dates come back.
            //
            // a side effect of suspense queries is that this hook (useGetForecastDates) cannot be
            // conditionally called, but there are places where we do not need the data from the api
            // (like in the date picker, which is written to handle both creative and media value
            // pages. for the creative version of the date picker we dont need forecast dates so an
            // api request would be useless). because of that i am returning today's date for all 3
            // data points we need if the hook is in "disabled" mode. the data in disabled mode
            // won't actually be used. this is not ideal but i would like to avoid major refactors
            if (!enabled) {
                return {
                    forecastCutoff: now(),
                    forecastCutoffDigital: now(),
                    maxDate: now()
                };
            }

            if (country === AU) {
                const response = await request(GetForecastDates, {queryOpensearch});
                return response.mediaValue.getForecastDates;
            }

            if (country === NZ) {
                const response = await request(GmvStatsQuery, {country: 'NZ'});
                const latestDate = response.globalMediaValue?.stats?.latestDate;
                if (!latestDate) {
                    throw new Error('Global Media Value stats latestDate not returned from API');
                }
                return {
                    forecastCutoff: latestDate,
                    forecastCutoffDigital: latestDate,
                    maxDate: latestDate
                };
            }

            return {
                forecastCutoff: now(),
                forecastCutoffDigital: now(),
                maxDate: now()
            };
        }
    });
}

// helpers

// This is a bit hacky but making all date pickers exclusive
// turned out to be too much work. So this just mutates the end date to
// be one more than asked for.
function makeDateRangeExclusive(dateRangeFilter: MediaValueDateRangeFilter) {
    return {
        ...dateRangeFilter,
        ...(dateRangeFilter?.absolute && {
            absolute: {
                startDate: dateRangeFilter.absolute.startDate,
                endDate: startOf('month')(add('P1M')(dateRangeFilter.absolute.endDate))
            }
        })
    };
}

function makeGmvDateRangeExclusive(dateRangeFilter: GlobalMediaValueDateRangeFilter) {
    return {
        ...dateRangeFilter,
        ...(dateRangeFilter?.absolute && {
            absolute: {
                startDate: dateRangeFilter.absolute.startDate,
                endDate: startOf('month')(add('P1M')(dateRangeFilter.absolute.endDate))
            }
        })
    };
}

function makePeriodSelectionExclusive(periodSelection?: PeriodSelection) {
    return {
        ...periodSelection,
        reportingPeriod: {
            ...periodSelection?.reportingPeriod,
            absolute: periodSelection?.reportingPeriod.absolute
                ? makeDateRangeExclusive({absolute: periodSelection.reportingPeriod.absolute})
                      .absolute
                : undefined
        },
        comparisonPeriod: periodSelection?.comparisonPeriod
            ? {
                  ...periodSelection?.comparisonPeriod,
                  absolute: periodSelection.comparisonPeriod.absolute
                      ? makeDateRangeExclusive({
                            absolute: periodSelection.comparisonPeriod.absolute
                        }).absolute
                      : undefined
              }
            : undefined
    };
}

function removeNonUsedColumns(rows: MediaValueRowFilterInput[]) {
    return rows.map((row: MediaValueRowFilterInput) => {
        return {
            id: row.id,
            label: row.label,
            filter: row.filter
        };
    });
}
